import '@mdxeditor/editor/style.css';
import React, { useCallback, useEffect } from 'react';
import { useAsyncDebounce } from 'react-table';
import clsx from 'clsx';
import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  InsertTable,
  InsertThematicBreak,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor';

export const GrowegyTextEditor = ({
  setValue,
  initialValue,
  disabled,
  readOnly,
  isInvalid,
  errorText,
  isError,
  errorClassName,
  debounceMs,
  dataTest,
  className,
  placeholder,
}: {
  setValue: (s: string) => void;
  initialValue: string;
  disabled: boolean;
  readOnly: boolean;
  isInvalid: boolean;
  errorText?: string;
  isError?: boolean;
  errorClassName?: string;
  debounceMs?: number;
  dataTest?: string;
  className?: string;
  placeholder?: string;
}) => {
  const onChange = useAsyncDebounce((v) => {
    setValue(v);
  }, debounceMs ?? 500);

  const editorRef = React.useRef<MDXEditorMethods>(null);
  useEffect(() => {
    editorRef.current?.setMarkdown(initialValue);
  }, [initialValue]);

  const handleChange = useCallback(
    (v: string) => {
      onChange(v);
    },
    [onChange]
  );
  return (
    <div
      data-test={dataTest}
      className={clsx('growegy-text-editor__input', className, {
        'is-invalid': isInvalid,
        'no-border': readOnly,
      })}
    >
      <>
        <MDXEditor
          ref={editorRef}
          markdown={initialValue}
          className={clsx('growegy-text-editor__part', {
            'is-invalid': isInvalid,
          })}
          placeholder={disabled || readOnly ? '' : placeholder}
          readOnly={readOnly || disabled}
          plugins={[
            headingsPlugin(),
            linkDialogPlugin(),
            linkPlugin(),
            listsPlugin(),
            markdownShortcutPlugin(),
            quotePlugin(),
            tablePlugin(),
            thematicBreakPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <Separator />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <InsertThematicBreak />
                  <ListsToggle />
                  <Separator />
                  <InsertTable />
                </>
              ),
            }),
          ]}
          onChange={handleChange}
        />
        {isError && errorText && (
          <div
            className={errorClassName ?? 'invalid-feedback'}
            data-test={`${dataTest}-validation-error`}
          >
            {errorText}
          </div>
        )}
      </>
    </div>
  );
};
